import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output, input, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AccountService } from 'projects/login/src/app/services/auth/account.service';
import { AuthSetPasswordResponse } from 'projects/login/src/app/services/auth/models/auth-set-password-response';
import { PayErrorResponse } from 'projects/login/src/app/services/auth/models/pay-error-response';
import { TokenService } from 'projects/login/src/app/services/auth/token.service';
import { forkJoin } from 'rxjs';
import { ButtonPrimaryComponent } from '../../button-primary/button-primary.component';
import { ErrorBadgeComponent } from '../../error-badge/error-badge.component';
import { InputComponent } from '../../input/input.component';
import { IconNames } from '../../pay-icon/IconNames';
import { PasswordResetRequirementComponent } from './password-reset-requirement/password-reset-requirement.component';

@Component({
  selector: 'pay-login-password-reset',
  imports: [InputComponent, TranslateModule, ReactiveFormsModule, ButtonPrimaryComponent, NgClass, ErrorBadgeComponent, PasswordResetRequirementComponent],
  templateUrl: './password-reset.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordResetComponent implements OnInit {
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _accService = inject(AccountService);
  private readonly _translateService = inject(TranslateService);
  private readonly _token = inject(TokenService);

  readonly token = input.required<string>();
  @Output() passwordResetDone = new EventEmitter<{ response: AuthSetPasswordResponse; password: string }>();

  newPassword = new FormControl<string>('', [Validators.required]);
  isSubmitting = false;

  password: string = '';
  error?: PayErrorResponse;

  hasUppercase = false;
  hasLowercase = false;
  hasNumber = false;
  hasSymbol = false;
  hasCharacters = false;

  private _bgColorFirstBar: 'bg-neutral-8' | 'bg-negative-100' | 'bg-informative-100' | 'bg-success-100' = 'bg-neutral-8';
  private _bgColorSecondBar: 'bg-neutral-8' | 'bg-informative-100' | 'bg-success-100' = 'bg-neutral-8';
  private _bgColorThirdBar: 'bg-neutral-8' | 'bg-success-100' = 'bg-neutral-8';

  get bgColorFirstBar() {
    return this._bgColorFirstBar;
  }

  get bgColorText() {
    return this.getColorText();
  }

  get bgColorSecondBar() {
    return this._bgColorSecondBar;
  }

  get bgColorThirdBar() {
    return this._bgColorThirdBar;
  }

  protected readonly IconNames = IconNames;

  ngOnInit() {
    this.newPassword.setValue('');

    this.newPassword.valueChanges.subscribe((password) => {
      if (password == null) {
        return;
      }
      this.password = password;

      this.hasCharacters = password.length > 7;
      this.hasUppercase = /[A-Z]/.test(password);
      this.hasLowercase = /[a-z]/.test(password);
      this.hasNumber = /[0-9]/.test(password);
      this.hasSymbol = /[!@#$]/.test(password);
      this.setColorsOfBars();
      this._cdRef.detectChanges();
    });
  }

  isValid() {
    return this.bgColorSecondBar === 'bg-informative-100' || this.bgColorSecondBar === 'bg-success-100';
  }

  submit() {
    if (this.isValid()) {
      this.isSubmitting = true;
      this._cdRef.detectChanges();

      forkJoin({
        passReset: this._accService.setPassword({
          passwordToken: this.token(),
          password: this.password,
        }),
        newSession: this._token.createDefaultSession(),
      }).subscribe({
        next: (response) => {
          this.passwordResetDone.emit({ response: response.passReset, password: this.password });
          TokenService.sessionId = response.newSession.sessionId;
          this.isSubmitting = false;
          this._cdRef.detectChanges();
        },
        error: (err) => {
          this.error = err.error as PayErrorResponse;
          this.isSubmitting = false;

          this._cdRef.detectChanges();
        },
      });
    }
  }

  private setColorsOfBars() {
    this._bgColorFirstBar = 'bg-neutral-8';
    this._bgColorSecondBar = 'bg-neutral-8';
    this._bgColorThirdBar = 'bg-neutral-8';

    if (this.password.length > 0) {
      this._bgColorFirstBar = 'bg-negative-100';

      if (this.hasUppercase && this.hasLowercase && this.hasNumber && this.hasSymbol && this.hasCharacters) {
        this._bgColorFirstBar = 'bg-informative-100';
        this._bgColorSecondBar = 'bg-informative-100';

        if (this.password.length > 11) {
          this._bgColorFirstBar = 'bg-success-100';
          this._bgColorSecondBar = 'bg-success-100';
          this._bgColorThirdBar = 'bg-success-100';
        }
      }
    }
  }

  private getColorText() {
    if (this.bgColorFirstBar === 'bg-informative-100') {
      return this._translateService.instant('password-reset.create-password-requirements.password-good.label');
    } else if (this.bgColorFirstBar === 'bg-success-100') {
      return this._translateService.instant('password-reset.create-password-requirements.password-strong.label');
    } else if (this.bgColorFirstBar === 'bg-negative-100') {
      return this._translateService.instant('password-reset.create-password-requirements.password-to-weak.label');
    } else {
      return this._translateService.instant('password-reset.create-password-requirements.label');
    }
  }
}
